import { FC, useRef, useState } from "react";
import clsx from "clsx";
import { useSearchParams } from "@remix-run/react";

import { MenuShort } from "~/entities/menuEntities";
import { useOutsideClick } from "~/hooks/useOutsideClick";

import styles from "./DineInMenuDropdown.module.css";

/**
 * DineInMenuDropdown — a styled menu switcher for the dine-in top bar (replaces
 * the native <select>, whose OS dropdown looked out of place). Shows the current
 * menu; opens a card listing the store's menus with the current one ticked.
 */
type Props = {
  menus: MenuShort[];
  currentCode?: string;
  accent: string;
};

const DineInMenuDropdown: FC<Props> = ({ menus, currentCode, accent }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [, setSearchParams] = useSearchParams();
  useOutsideClick([ref], () => setOpen(false));

  const current = menus.find((m) => m.code === currentCode);

  const select = (code: string) => {
    setSearchParams((x) => {
      x.set("menuCode", code);
      return x;
    });
    setOpen(false);
  };

  return (
    <div className={styles.wrap} ref={ref}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={styles.triggerLabel}>{current?.name ?? "Menu"}</span>
        <svg
          className={clsx(styles.chevron, open && styles.chevronOpen)}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="3 6 8 11 13 6" />
        </svg>
      </button>

      {open && (
        <ul className={styles.menu} role="listbox">
          {menus.map((m) => {
            const active = m.code === currentCode;
            return (
              <li key={m.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={clsx(styles.item, active && styles.itemActive)}
                  style={active ? { color: accent } : undefined}
                  onClick={() => select(m.code)}
                >
                  <span className={styles.itemLabel}>{m.name}</span>
                  {active && (
                    <svg
                      className={styles.check}
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke={accent}
                      strokeWidth={2.4}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="3 8 7 12 13 4" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default DineInMenuDropdown;
